import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import LocalizedNumberInput from '../components/ui/LocalizedNumberInput';
import { parseLocalizedNumber } from '../utils/numberFormat';
import { buildLocalOperationTimestamp, getLocalDateKey } from '../utils/localDateTime';
import { ArrowLeft, DollarSign, FileText, AlertTriangle, RefreshCcw, Landmark, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';

const fmtMoney = (value) =>
  Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function BancosMovimiento({ onNavigate }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    tipo: 'Ingreso',
    monto: '',
    obs: '',
    fecha: getLocalDateKey()
  });

  const goBack = () => {
    if (onNavigate) return onNavigate('bancos');
    navigate('/sistema/bancos');
  };

  useEffect(() => {
    if (!id || !profile?.organization_id) return;
    loadBank();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, profile?.organization_id]);

  const loadBank = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('banks')
        .select('id, name, currency, current_balance, cbu_alias')
        .eq('organization_id', profile.organization_id)
        .eq('id', id)
        .single();

      if (error) throw error;
      setBank(data);
    } catch (error) {
      console.error(error);
      toast.error('No se pudo cargar la cuenta bancaria');
      goBack();
    } finally {
      setLoading(false);
    }
  };

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);

    let saldoAnterior = null;

    try {
      const monto = parseLocalizedNumber(form.monto);
      if (!monto || monto <= 0) {
        toast.error('Ingresá un monto valido');
        return;
      }

      saldoAnterior = Number(bank?.current_balance || 0);
      const esIngreso = form.tipo === 'Ingreso';
      const saldoNuevo = esIngreso ? saldoAnterior + monto : saldoAnterior - monto;
      const fechaOperacion = buildLocalOperationTimestamp(form.fecha);
      const descripcion = form.obs?.trim()
        ? `Ajuste manual banco: ${form.obs.trim()}`
        : 'Ajuste manual de saldo bancario';

      const { error: updateError } = await supabase
        .from('banks')
        .update({ current_balance: saldoNuevo })
        .eq('id', id);

      if (updateError) throw updateError;

      const { error: movError } = await supabase
        .from('movimientos_financieros')
        .insert([
          {
            organization_id: profile.organization_id,
            bank_id: id,
            tipo: form.tipo,
            categoria: 'Ajuste Banco',
            monto,
            saldo_resultante: saldoNuevo,
            entidad_tipo: 'Banco',
            entidad_id: id,
            documento_tipo: 'Ajuste Banco',
            descripcion,
            fecha_operacion: fechaOperacion,
            user_id: profile?.id || profile?.user_id || null,
            is_active: true
          }
        ]);

      if (movError) {
        await supabase.from('banks').update({ current_balance: saldoAnterior }).eq('id', id);
        throw movError;
      }

      toast.success('Ajuste bancario registrado');
      goBack();
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar el ajuste bancario');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={34} />
        <p className="font-black uppercase tracking-[0.25em] text-slate-400 text-[10px]">Sincronizando banco...</p>
      </div>
    );
  }

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3 px-1">
        <div className="flex items-center gap-3">
          <button
            onClick={goBack}
            className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Landmark size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-800 tracking-tight uppercase">Ajuste Bancario</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Movimiento manual para {bank?.name || 'cuenta bancaria'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 mb-3">
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cuenta</p>
            <Landmark size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight truncate">{bank?.name || '-'}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{bank?.cbu_alias || bank?.currency || 'Cuenta activa'}</p>
        </div>
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Saldo actual</p>
            <DollarSign size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight">$ {fmtMoney(bank?.current_balance)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Antes del ajuste</p>
        </div>
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm sm:col-span-2 xl:col-span-1">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Operacion</p>
            <FileText size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight">{form.tipo}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Impacto inmediato en tesoreria</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-slate-50/50 px-4 py-3 sm:px-5 border-b border-slate-100 flex items-center gap-3">
          <FileText size={16} className="text-[#51A7FD]" />
          <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-500">Carga de ajuste</h3>
        </div>

        <form onSubmit={guardar} className="p-4 sm:p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 shrink-0">
              <AlertTriangle size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase text-amber-800 tracking-widest mb-1">Aviso de impacto financiero</p>
              <p className="text-[11px] font-medium text-amber-700 leading-relaxed">
                Los ingresos aumentan el saldo del banco y los egresos lo reducen. Este ajuste queda registrado en el historial financiero.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Tipo de operacion</label>
              <select
                className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-[11px] font-black uppercase outline-none focus:border-[#51A7FD]"
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              >
                <option value="Ingreso">Ingreso</option>
                <option value="Egreso">Egreso</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Importe total</label>
              <div className="relative">
                <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <LocalizedNumberInput
                  decimals={2}
                  selectOnFocus
                  className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 pl-10 pr-4 text-[11px] font-black text-slate-900 outline-none focus:border-[#51A7FD]"
                  placeholder="0,00"
                  value={form.monto}
                  onValueChange={(display) => setForm({ ...form, monto: display })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Fecha</label>
              <div className="relative">
                <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                  type="date"
                  className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 pl-10 pr-4 text-[11px] font-black text-slate-900 outline-none focus:border-[#51A7FD]"
                  value={form.fecha}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Observaciones</label>
            <textarea
              className="w-full bg-slate-50 border border-slate-100 rounded-xl p-4 text-[11px] font-bold text-slate-600 outline-none focus:border-[#51A7FD] h-28 resize-none"
              placeholder="Ej: Ajuste por diferencia entre extracto y sistema..."
              value={form.obs}
              onChange={(e) => setForm({ ...form, obs: e.target.value })}
            />
          </div>

          <Button
            type="submit"
            isLoading={saving}
            className="w-full h-12 bg-[#0B2A36] text-white rounded-2xl shadow-lg hover:scale-[1.01] transition-all active:scale-95 flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] disabled:opacity-50"
          >
            Confirmar ajuste
          </Button>
        </form>
      </div>
    </div>
  );
}

export default BancosMovimiento;
